import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { supabase } from './services/supabase';
import { useAuthStore, type AdminProfile } from './store/authStore';
import type { Database } from './types/database.types';
import AdminLayout from './components/layout/AdminLayout';
import MonitorLayout from './components/layout/MonitorLayout';
import LoginPage from './pages/LoginPage';
import SetPasswordPage from './pages/SetPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import WelcomePage from './pages/WelcomePage';
import DashboardPage from './pages/DashboardPage';
import AlumnosPage from './pages/AlumnosPage';
import FamiliasPage from './pages/FamiliasPage';
import MonitoresPage from './pages/MonitoresPage';
import GruposPage from './pages/GruposPage';
import SesionesPage from './pages/SesionesPage';
import SuscripcionesPage from './pages/SuscripcionesPage';
import BonosPage from './pages/BonosPage';
import BonosManualesPage from './pages/BonosManualesPage';
import TiendaPage from './pages/TiendaPage';
import InventarioPage from './pages/InventarioPage';
import SponsorsPage from './pages/SponsorsPage';
import CollaboratorsPage from './pages/CollaboratorsPage';
import VideoAnalysisPage from './pages/VideoAnalysisPage';
import TecnificacionIndividualPage from './pages/TecnificacionIndividualPage';
import PedidosPage from './pages/PedidosPage';
import ComunicacionesPage from './pages/ComunicacionesPage';
import EvaluacionesPage from './pages/EvaluacionesPage';
import ValoracionesPage from './pages/ValoracionesPage';
import EstadisticasPage from './pages/EstadisticasPage';
import ConfiguracionPage from './pages/ConfiguracionPage';
import AlumnoDetallePage from './pages/AlumnoDetallePage';
import ContabilidadPage from './pages/ContabilidadPage';
import AnaliticaPage from './pages/AnaliticaPage';
import NegocioPage from './pages/NegocioPage';
import GestionPage from './pages/GestionPage';
import SedesPage from './pages/SedesPage';
import DescuentosPage from './pages/DescuentosPage';
import GymPage from './pages/GymPage';
import NoticiasPage from './pages/NoticiasPage';
import NotificacionesPage from './pages/NotificacionesPage';
import DirectoresPage from './pages/DirectoresPage';
import MonitorDashboardPage from './pages/monitor/MonitorDashboardPage';
import MonitorAgendaPage from './pages/monitor/MonitorAgendaPage';
import MonitorSessionDetailPage from './pages/monitor/MonitorSessionDetailPage';
import MonitorEvaluacionesPage from './pages/monitor/MonitorEvaluacionesPage';
import MonitorComunicacionesPage from './pages/monitor/MonitorComunicacionesPage';
import MonitorNotificacionesPage from './pages/monitor/MonitorNotificacionesPage';
import MonitorGymPage from './pages/monitor/MonitorGymPage';
import MonitorGymAlumnoPage from './pages/monitor/MonitorGymAlumnoPage';

// El director hereda el acceso admin (decisión #4): entra al panel igual que
// un admin. La frontera root-only se aplica SOLO en la página de gestión de
// directores (RootAdminOnly + RPC/EF root-only).
const ALLOWED_ROLES = ['admin', 'director', 'monitor'] as const;

type ProfileQueryRow = Pick<
  Database['public']['Tables']['profiles']['Row'],
  'id' | 'email' | 'full_name' | 'role' | 'avatar_url' | 'is_physical_trainer' | 'active'
>;

function toAdminProfile(row: ProfileQueryRow | null): AdminProfile | null {
  if (!row) return null;
  // Cuenta anonimizada (7.F1) o desactivada → no entra al admin. La EF 7.F2
  // borrará `auth.users` cuando proceda; mientras tanto bloqueamos aquí.
  if (row.active === false) return null;
  const role = row.role as AdminProfile['role'];
  if (!ALLOWED_ROLES.includes(role)) return null;
  return {
    id: row.id,
    email: row.email,
    full_name: row.full_name,
    avatar_url: row.avatar_url,
    is_physical_trainer: row.is_physical_trainer,
    role,
  };
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { session, profile, loading } = useAuthStore();

  // Enforcement de acceso: aquí, en la frontera protegida, NO en el listener
  // global (que solo observa y publica estado). Una sesión con perfil inválido
  // (inactivo o rol no admitido → profile=null tras la carga) es una sesión
  // huérfana: la cerramos y el render redirige a /login. Las rutas públicas
  // (/set-password, /reset-password, /welcome) NO montan ProtectedRoute, así
  // que un invitado nunca es expulsado mientras define su contraseña.
  useEffect(() => {
    if (!loading && session && !profile) {
      void supabase.auth.signOut();
    }
  }, [loading, session, profile]);

  if (loading) {
    return (
      <div className="min-h-screen bg-bg-primary flex items-center justify-center">
        <div className="text-gold-primary font-oswald text-xl animate-pulse">
          VERTEX Performance
        </div>
      </div>
    );
  }

  if (!session) return <Navigate to="/login" replace />;
  // Sesión huérfana (perfil inválido): el useEffect de arriba ya la está
  // cerrando; redirigimos a /login para no renderizar el shell protegido.
  if (!profile) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

// Admin Y director ven el panel admin (director hereda el acceso). Solo el
// monitor es redirigido a su portal.
export function AdminOnly({ children }: { children: React.ReactNode }) {
  const { profile, loading } = useAuthStore();
  if (loading) return null;
  if (!profile) return <Navigate to="/login" replace />;
  if (profile.role === 'monitor') return <Navigate to="/monitor/dashboard" replace />;
  return <>{children}</>;
}

// Solo el monitor entra al portal monitor. Admin Y director (no-monitor) se
// redirigen al panel admin.
export function MonitorOnly({ children }: { children: React.ReactNode }) {
  const { profile, loading } = useAuthStore();
  if (loading) return null;
  if (!profile) return <Navigate to="/login" replace />;
  if (profile.role !== 'monitor') return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

// Gestión de directores: ROOT-ONLY (role='admin' estricto). Un director NO
// puede acceder (ni ver ni actuar) — se le redirige al panel. Defensa en
// capas: además del gate de ruta, la RPC admin_deactivate_director y la EF
// create-director son root-only (mig 145 / PR-D2).
export function RootAdminOnly({ children }: { children: React.ReactNode }) {
  const { profile, loading } = useAuthStore();
  if (loading) return null;
  if (!profile) return <Navigate to="/login" replace />;
  if (profile.role !== 'admin') return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

function RoleRedirect() {
  const { profile } = useAuthStore();
  if (profile?.role === 'monitor') return <Navigate to="/monitor/dashboard" replace />;
  return <Navigate to="/dashboard" replace />;
}

export default function App() {
  const { setSession, setProfile, setLoading } = useAuthStore();

  useEffect(() => {
    let initialized = false;
    // Hoisteado al scope del efecto para poder limpiarlo también en el cleanup
    // (si el componente se desmonta antes de que initAuth termine, el timer de
    // 15s quedaría vivo). En el camino normal se limpia en el `finally`.
    let globalTimer: ReturnType<typeof setTimeout> | undefined;

    async function initAuth() {
      // 15s — antes 6s, debe ser >> el timeout interno del profile fetch
      // (12s) para que NO se dispare ANTES de que initAuth termine y caiga
      // en la rama `setProfile(adminProfile)`. Si globalTimer salta primero,
      // loading=false sin profile → AdminOnly redirige.
      globalTimer = setTimeout(() => {
        initialized = true;
        setLoading(false);
      }, 15000);

      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        setSession(session);
        if (session) {
          // 12s — antes 5s, demasiado agresivo en CI con Vite dev sirviendo
          // múltiples módulos JS en paralelo: la query a profiles a veces
          // tardaba >5s, el timeout ganaba, row=null y la rama "else if (row)"
          // tampoco ejecuta `signOut` (queda profile=null + loading=false →
          // AdminOnly redirige a /login). 8.2 lo destapó al añadir flows
          // que navegan tras login.
          const timeoutResult = new Promise<{ data: null }>((resolve) =>
            setTimeout(() => resolve({ data: null }), 12000)
          );
          const profileQuery = supabase
            .from('profiles')
            .select('id, email, full_name, role, avatar_url, is_physical_trainer, active')
            .eq('id', session.user.id)
            .single();
          const result = await Promise.race([profileQuery, timeoutResult]);
          const row = result.data as ProfileQueryRow | null;
          const adminProfile = toAdminProfile(row);
          if (adminProfile) {
            setProfile(adminProfile);
          } else if (row) {
            // Observación, no expulsión: publicamos profile=null y NO cerramos
            // sesión aquí. El enforcement (signOut + redirect) vive en
            // ProtectedRoute, para no expulsar sesiones legítimas de onboarding
            // en las rutas públicas /set-password y /reset-password.
            setProfile(null);
          }
        }
      } catch {
        setSession(null);
        setProfile(null);
      } finally {
        clearTimeout(globalTimer);
        initialized = true;
        setLoading(false);
      }
    }

    initAuth();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!initialized && event === 'INITIAL_SESSION') return;

      setSession(session);
      if (session) {
        try {
          // 12s — antes 5s, demasiado agresivo en CI con Vite dev sirviendo
          // múltiples módulos JS en paralelo: la query a profiles a veces
          // tardaba >5s, el timeout ganaba, row=null y la rama "else if (row)"
          // tampoco ejecuta `signOut` (queda profile=null + loading=false →
          // AdminOnly redirige a /login). 8.2 lo destapó al añadir flows
          // que navegan tras login.
          const timeoutResult = new Promise<{ data: null }>((resolve) =>
            setTimeout(() => resolve({ data: null }), 12000)
          );
          const profileQuery = supabase
            .from('profiles')
            .select('id, email, full_name, role, avatar_url, is_physical_trainer, active')
            .eq('id', session.user.id)
            .single();
          const result = await Promise.race([profileQuery, timeoutResult]);
          const row = result.data as ProfileQueryRow | null;
          const adminProfile = toAdminProfile(row);
          if (adminProfile) {
            setProfile(adminProfile);
          } else if (row) {
            // Observación, no expulsión (ver initAuth): profile=null sin cerrar
            // sesión. El enforcement vive en ProtectedRoute.
            setProfile(null);
          }
        } catch {
          setProfile(null);
        }
      } else {
        setProfile(null);
      }
      setLoading(false);
    });

    return () => {
      clearTimeout(globalTimer);
      subscription.unsubscribe();
    };
    // Efecto único de bootstrap de auth: corre 1 vez al montar y suscribe el
    // listener. Las deps reportadas (setSession/setProfile/setLoading) son
    // setters de `useState`, estables por contrato de React — añadirlas no
    // cambia el comportamiento.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        {/* Ruta pública: el monitor invitado aterriza aquí desde el email
            de invitación; Supabase JS monta la sesión desde el hash y la
            página le pide definir contraseña antes de entrar al portal. */}
        <Route path="/set-password" element={<SetPasswordPage />} />
        {/* Ruta pública: destino del enlace del email de RECUPERACIÓN
            (resetPasswordForEmail). Supabase JS monta la sesión de recovery
            desde el hash; la página fija la nueva contraseña, hace signOut y
            devuelve a /login para re-aplicar el gate de acceso. */}
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        {/* Ruta pública: landing post-set-password (§3c). Greeter por
            nombre + 1-2 next steps + CTA al dashboard del role. */}
        <Route path="/welcome" element={<WelcomePage />} />

        {/* Admin routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <AdminOnly>
                <AdminLayout />
              </AdminOnly>
            </ProtectedRoute>
          }
        >
          <Route index element={<RoleRedirect />} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="negocio" element={<NegocioPage />} />
          <Route path="gestion" element={<GestionPage />} />
          <Route path="sedes" element={<SedesPage />} />
          <Route path="descuentos" element={<DescuentosPage />} />
          <Route path="alumnos" element={<AlumnosPage />} />
          <Route path="alumnos/:id" element={<AlumnoDetallePage />} />
          <Route path="familias" element={<FamiliasPage />} />
          <Route path="monitores" element={<MonitoresPage />} />
          <Route path="sesiones" element={<GruposPage />} />
          <Route path="agenda" element={<SesionesPage />} />
          <Route path="bonos" element={<BonosPage />} />
          <Route path="bonos-manuales" element={<BonosManualesPage />} />
          <Route path="gym" element={<GymPage />} />
          <Route path="gym/exercises" element={<GymPage initialTab="exercises" />} />
          <Route path="gym/assignments" element={<GymPage initialTab="assignments" />} />
          <Route path="tienda" element={<TiendaPage />} />
          <Route path="inventario" element={<InventarioPage />} />
          <Route path="suscripciones" element={<SuscripcionesPage />} />
          <Route path="patrocinadores" element={<SponsorsPage />} />
          <Route path="colaboradores" element={<CollaboratorsPage />} />
          <Route path="video-analisis" element={<VideoAnalysisPage />} />
          <Route path="tecnificacion-individual" element={<TecnificacionIndividualPage />} />
          <Route path="pedidos" element={<PedidosPage />} />
          <Route path="comunicaciones" element={<ComunicacionesPage />} />
          <Route path="noticias" element={<NoticiasPage />} />
          <Route path="notificaciones" element={<NotificacionesPage />} />
          <Route path="evaluaciones" element={<EvaluacionesPage />} />
          <Route path="valoraciones" element={<ValoracionesPage />} />
          <Route path="analitica" element={<AnaliticaPage />} />
          <Route path="contabilidad" element={<ContabilidadPage />} />
          <Route path="estadisticas" element={<EstadisticasPage />} />
          <Route path="configuracion" element={<ConfiguracionPage />} />
          {/* Gestión de directores: ROOT-ONLY (un director queda fuera). */}
          <Route
            path="directores"
            element={
              <RootAdminOnly>
                <DirectoresPage />
              </RootAdminOnly>
            }
          />
        </Route>

        {/* Monitor routes */}
        <Route
          path="/monitor"
          element={
            <ProtectedRoute>
              <MonitorOnly>
                <MonitorLayout />
              </MonitorOnly>
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/monitor/dashboard" replace />} />
          <Route path="dashboard" element={<MonitorDashboardPage />} />
          <Route path="agenda" element={<MonitorAgendaPage />} />
          <Route path="sesion/:sessionId" element={<MonitorSessionDetailPage />} />
          <Route path="gym" element={<MonitorGymPage />} />
          <Route path="gym/:userId" element={<MonitorGymAlumnoPage />} />
          <Route path="evaluaciones" element={<MonitorEvaluacionesPage />} />
          <Route path="comunicaciones" element={<MonitorComunicacionesPage />} />
          {/* §128 · La campana del entrenador. `/notificaciones` existía desde
              siempre, pero DENTRO del bloque AdminOnly: al monitor lo redirigía
              a su dashboard, así que sus avisos eran inalcanzables. */}
          <Route path="notificaciones" element={<MonitorNotificacionesPage />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
